import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppConstants } from 'src/environments/constants';
import { RoleUtils } from 'src/utils/RoleUtils';
import { AuthService } from './authentication/auth.service';
import { RolePermissions } from './model/rolePermissions';
import { TipologiaUtente } from './model/utente/utente';
import { DashboardComponent } from './pages/dashboard/components/dashboard.component';
import { NotificheService } from './services/notifiche/notifiche.service';
import { AppUtilsService } from './shared/services/app-utils.service';
import { filter } from 'rxjs/operators';
import { Css, patchCss } from './shared/functions/ovverideCss';
import '@ds/ds-snam';
import { BreadcrumbService } from './services/breadcrumb.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit {
  title = 'edith-fe';
  @ContentChild(DashboardComponent) child: any;
  isAdmin = false;
  isAuthenticated = false;
  isUserNotValid = false;
  currLang = 'it';

  utenteLoggato: any;
  role: any;
  permissions: RolePermissions;
  errorMsg: String = '';
  message: string = '';
  dashboard: any;
  isSpinnerLoaded = false;
  myBreadcrumbData: any = [];
  currentUrl = '/';
  customLabel: string | null = null;
  isHome: boolean = false;
  @ViewChild('breadcrumb') breadcrumb: ElementRef;
  private tableObserver: MutationObserver;
  breadcrumbListActive = false;

  constructor(
    private translate: TranslateService,
    private router: Router,
    private authService: AuthService,
    private notificheServ: NotificheService,
    private appUtilsService: AppUtilsService,
    private breadcrumbService: BreadcrumbService
  ) {
    this.translate.addLangs(['it', 'en']);
    this.translate.setDefaultLang('it');
    this.permissions = new RolePermissions();

    this.authService.isAuthenticated$.subscribe(isAuth => {
      if (isAuth) {
        this.isAuthenticated = true;
        this.utenteLoggato = this.authService.getUtenteLoggato();
        this.role = this.utenteLoggato.tipologiaUtente;
        this.isAdmin = this.role === TipologiaUtente.ADMIN;
        if (this.isAdmin) {
          console.log('ADM IN');
          this.router.navigate([AppConstants.ADMIN_PAGE]);
        } else {
          console.log('non admin');
          this.router.initialNavigation();
        }

        if (this.utenteLoggato == null) {
          this.isUserNotValid = true;
          this.errorMsg = 'Utente non abilitato';
        }

        this.permissions = this.checkRole(this.role);
      }
    });

    this.authService.isUserError$.subscribe(isErr => {
      if (isErr != null) {
        this.isUserNotValid = true;
        this.errorMsg = isErr;
        this.router.initialNavigation();
        this.router.navigate([AppConstants.UTENTE_NON_ABILITATO_ROUTE]);
      }
    });

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.urlAfterRedirects;
        this.buildBreadcrumb(this.currentUrl);
      });

    this.breadcrumbService.getCustomLabel().subscribe(label => {
      if (this.breadcrumbListActive) return;
      if (this.currentUrl === '/' || this.currentUrl.startsWith('/dashboard')) {
        this.myBreadcrumbData = [];
        return;
      }

      this.customLabel = label;
      this.buildBreadcrumb(this.currentUrl);
    });

    this.breadcrumbService.getBreadcrumb().subscribe(list => {
      this.breadcrumbListActive = !!list;
      if (list) {
        setTimeout(() => {
          this.myBreadcrumbData = list.map(item => ({
            title: item.label,
            url: item.url,
            isRouterLink: !!item.url,
          }));
        });
        return;
      }

      this.customLabel = null;
      this.buildBreadcrumb(this.currentUrl);
    });
  }

  buildBreadcrumb(url: string) {
    if (url === '/' || url.startsWith('/dashboard')) {
      this.myBreadcrumbData = [];
      return;
    }

    const segments = url.split('/').filter(Boolean);

    let breadcrumbs = segments.map((segment, index) => ({
      title: this.formatLabel(segment, index, segments),
      url: '/' + segments.slice(0, index + 1).join('/'),
      isRouterLink: true,
    }));

    if (this.customLabel && breadcrumbs.length > 0) {
      breadcrumbs[breadcrumbs.length - 1] = {
        ...breadcrumbs[breadcrumbs.length - 1],
        title: this.customLabel,
        isRouterLink: false,
      };
    }

    this.myBreadcrumbData = [
      { title: 'Home', url: '/', isRouterLink: true },
      ...breadcrumbs,
    ];
  }

  labelMap = {
    ordini: 'Ordini',
    svincoli: 'Svincoli',
    'non-conformita': 'NonConformita',
    ispezioni: 'Ispezioni',
    previewDocumento: 'Previewdocumento',
  };

  detailLabelMap: any = {
    ordini: 'DettaglioOrdine',
    svincoli: 'DettaglioSvincolo',
    'non-conformita': 'DettaglioNonConformita',
    ispezioni: 'DettaglioIspezioni',
    Previewdocumento: 'DettaglioPreviewDocumento',
  };
  formatLabel(segment: string, index: number, segments: string[]): string {
    if (!isNaN(Number(segment))) {
      const previous = segments[index - 1];
      if (previous === 'ordini') {
        const ordineInBreadcrumb =
          this.breadcrumbService.getFilterDettaglioOrdine()?.ordine;
        if (
          ordineInBreadcrumb &&
          Number(ordineInBreadcrumb.id) === Number(segment) &&
          ordineInBreadcrumb.numeroOrdine
        ) {
          return `${this.translate.instant('ordini.order')} ${ordineInBreadcrumb.numeroOrdine}`;
        }
        return this.translate.instant('ordini.order');
      }
      const lookupKey =
        previous === 'previewDocumento' ? 'Previewdocumento' : previous;
      const dettaglio = this.detailLabelMap[lookupKey] || 'Dettaglio';
      return this.translate.instant('breadcrumb.' + dettaglio);
    }

    if (segment === 'dashboard') {
      segment = 'Home';
    }

    const key =
      'breadcrumb.' +
      segment.charAt(0).toUpperCase() +
      segment.slice(1).toLowerCase();
    const translated = this.translate.instant(key);

    return translated !== key
      ? translated
      : segment.charAt(0).toUpperCase() + segment.slice(1);
  }

  ngAfterViewInit() {
    // this.patchBreadcrumb();
    this.patchAllTables();
    this.observeTables();
  }

  routerLinkHandler(event: any) {
    if(event.detail.url === '/'){
      this.breadcrumbService.resetBreadcrumbFilter();
      this.breadcrumbService.setFromBreadcrumb(false);
    }
    else{
      this.breadcrumbService.setFromBreadcrumb(true);
    }
    this.router.navigateByUrl(event.detail.url);
  }

  // patchBreadcrumb() {
  //   const shadow = this.breadcrumb?.nativeElement?.shadowRoot;
  //   if (shadow) {
  //     patchCss(shadow, this.cssValori);
  //   } else {
  //     setTimeout(() => this.patchBreadcrumb(), 50);
  //   }
  // }

  private readonly tableCssPatch: Css[] = [
    {
      selettore: '.ds-table__wrapper',
      keyValue: [
        {
          attributo: 'display',
          valore: 'table !important',
        },
      ],
    },
    {
      selettore:
        ':host(.dashboard-table) .ds-table__wrapper, :host(.scrollable-ds-table) .ds-table__wrapper, :host(.full-width-ds-table) .ds-table__wrapper',
      keyValue: [
        {
          attributo: 'display',
          valore: 'flex !important',
        },
        {
          attributo: 'overflow-x',
          valore: 'auto !important',
        },
        {
          attributo: 'overflow-y',
          valore: 'hidden !important',
        },
        {
          attributo: 'max-width',
          valore: '100% !important',
        },
      ],
    },
  ];

  private patchTableElement(el: Element): void {
    const shadow = (el as any).shadowRoot;
    if (shadow) {
      patchCss(shadow, this.tableCssPatch);
    } else {
      setTimeout(() => this.patchTableElement(el), 50);
    }
  }

  private patchAllTables(): void {
    document
      .querySelectorAll('ds-table')
      .forEach(el => this.patchTableElement(el));
  }

  private observeTables(): void {
    this.tableObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node instanceof HTMLElement) {
            if (node.tagName === 'DS-TABLE') {
              this.patchTableElement(node);
            }
            node
              .querySelectorAll('ds-table')
              .forEach(el => this.patchTableElement(el));
          }
        });
      });
    });
    this.tableObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  ngOnInit(): void {
    this.authService.login();
    this.breadcrumbService.clear();
    if (this.currentUrl !== '/dashboard')
      this.translate.onLangChange.subscribe(() => {
        setTimeout(() => {
          if (this.breadcrumbListActive) {
            return;
          }
          this.buildBreadcrumb(this.router.url);
        }, 50);
      });

      this.appUtilsService.getIsLoadSpinner().subscribe((isLoad) => {
      queueMicrotask(() => {
        this.isSpinnerLoaded = isLoad;
      });
    });

    this.breadcrumbService.initBreadcrumbFilter();
 
  }

  logoutClick() {
    this.authService.logout();
  }

  checkRole(role: any) {
    this.permissions = RoleUtils.checkRole(role);
    return this.permissions;
  }

  translateSite(language: string) {
    this.translate.use(language);
    console.log('lang', language);
    this.currLang = language;
    this.getNumberNotification();
  }

  getNumberNotification() {
    this.notificheServ.setResetTimer(true);

    this.notificheServ.getNumberNotification().subscribe(res => {
      let key;

      if (res > 1) {
        key = 'numberNotifications';
      } else if (res === 0) {
        key = 'noNotifications';
      } else {
        key = 'numberNotification';
      }

      this.translate.get(key, { number: res }).subscribe(message => {
        this.message = message;
        console.log(message);
        this.onOutletLoaded(this.dashboard);
      });
      if (res !== 0) {
        let key =
          res > 1 || res === 0 ? 'numberNotifications' : 'numberNotification';
        this.translate.get(key, { number: res }).subscribe(message => {
          this.message = message;
          this.onOutletLoaded(this.dashboard);
        });
      }
    });
  }

  onOutletLoaded(component: any) {
    console.log(component);
    this.dashboard = component;
    component.message = this.message;
  }
}
